import { Link, Navigate, useParams } from "react-router-dom";
import { ArrowRight, CheckSquare, Cpu, Layers, Package } from "lucide-react";
import { SiteNav } from "@/components/marketing/SiteNav";
import { SiteFooter } from "@/components/marketing/SiteFooter";
import { INDUSTRIES, industryBySlug } from "@/data/industries";
import { useHashScroll } from "@/pages/Landing";

const ListBlock = ({ icon: Icon, title, items, testid }) => (
  <div className="panel p-6" data-testid={testid}>
    <Icon className="w-5 h-5 text-amber-500 mb-4" />
    <h3 className="text-xl font-semibold text-slate-100 mb-4">{title}</h3>
    <ul className="space-y-2.5">
      {items.map((s) => <li key={s} className="flex gap-3 text-slate-300 text-sm"><span className="w-1.5 h-1.5 bg-amber-500 mt-2 shrink-0" />{s}</li>)}
    </ul>
  </div>
);

export default function IndustryPage() {
  const { slug } = useParams();
  useHashScroll();
  const ind = industryBySlug(slug);
  if (!ind) return <Navigate to="/" replace />;
  const demoHref = `/?industry=${ind.slug}#demo`;
  return (
    <div className="min-h-screen bg-[#080D1A]">
      <SiteNav />
      <section className="grid-bg border-b border-[#1E2D4A]" data-testid="industry-hero">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-16 md:py-24">
          <Link to="/#industries" className="font-mono text-xs text-slate-500 hover:text-amber-400">← All industries</Link>
          <p className="eyebrow mt-8 mb-4">{ind.name} · {ind.milestone}</p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-slate-50 max-w-4xl leading-[1.02]" data-testid="industry-headline">{ind.headline}</h1>
          <p className="mt-6 text-lg text-slate-300 max-w-3xl leading-relaxed">{ind.intro}</p>
          <Link to={demoHref} data-testid="industry-request-demo-btn" className="btn-accent inline-flex items-center gap-2 px-7 py-3.5 mt-9 group">
            Request a demo for {ind.name} <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>
      </section>
      <section className="max-w-7xl mx-auto px-5 lg:px-8 py-16 md:py-20 space-y-12">
        <div data-testid="industry-milestones">
          <p className="eyebrow mb-5">Milestones, in your language</p>
          <div className="flex flex-wrap items-center gap-2">
            {ind.milestones.map((m, i) => (
              <span key={m} className="flex items-center gap-2">
                <span className={`px-4 py-2 border text-sm font-medium ${m === ind.milestone ? "border-amber-500 text-amber-400 bg-amber-500/10" : "border-[#1E2D4A] text-slate-300"}`}>{m}</span>
                {i < ind.milestones.length - 1 && <ArrowRight className="w-4 h-4 text-slate-600" />}
              </span>
            ))}
          </div>
          <p className="text-sm text-slate-500 mt-4">Milestones are configurable per project. Each one defines which systems and stages must be complete.</p>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          <ListBlock icon={Layers} title="Typical systems" items={ind.systems} testid="industry-systems" />
          <ListBlock icon={Cpu} title="Typical equipment" items={ind.equipment} testid="industry-equipment" />
          <div className="panel p-6 border-amber-500/40" data-testid="industry-template-pack">
            <Package className="w-5 h-5 text-amber-500 mb-4" />
            <h3 className="text-xl font-semibold text-slate-100">Template pack included</h3>
            <p className="text-amber-400 font-medium mt-1">{ind.pack.name}</p>
            <p className="font-mono text-[11px] uppercase tracking-wider text-slate-500 mt-1">{ind.pack.available ? "Available now" : "Configured from existing packs"}</p>
            <ul className="space-y-2.5 mt-4">
              {ind.pack.sheets.map((s) => <li key={s} className="flex gap-3 text-slate-300 text-sm"><CheckSquare className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />{s}</li>)}
            </ul>
          </div>
        </div>
        <div className="panel p-8 flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div>
            <h2 className="text-2xl font-bold text-slate-100">See {ind.milestone} forecast on your own data.</h2>
            <p className="text-slate-400 mt-2">Send us a sample tag register and we'll show you the forecast working.</p>
          </div>
          <Link to={demoHref} data-testid="industry-bottom-demo-btn" className="btn-accent px-7 py-3.5 shrink-0 text-center">Request a demo</Link>
        </div>
        <div>
          <p className="eyebrow mb-4">Other industries</p>
          <div className="flex flex-wrap gap-2">
            {INDUSTRIES.filter((i) => i.slug !== ind.slug).map((i) => (
              <Link key={i.slug} to={`/industries/${i.slug}`} className="px-3 py-1.5 border border-[#1E2D4A] text-sm text-slate-400 hover:text-white hover:border-amber-500 transition-colors">{i.name}</Link>
            ))}
          </div>
        </div>
      </section>
      <SiteFooter />
    </div>
  );
}
